import {
  createBrowserRouter,
  Navigate,
  Outlet,
  ScrollRestoration,
  useLocation,
} from "react-router";
import InquiryPage from "./routes/inquiry";
import { InquiryReplayBoundary } from "./components/inquiry/InquiryReplayBoundary";
import TermsPage from "./routes/terms";
import PrivacyPage from "./routes/privacy";
import HomePage from "./routes/home";
import { isPublicSiteHost } from "./lib/public-site-host";

/**
 * Public-frontend route config. The public product surface is the inquiry
 * flow; marketing pages intentionally redirect to `/enquiry`.
 *
 * The token-gated pages — approve, pay-deposit, pay-balance,
 * collect-balance, pay-contractor, feedback, contractor-terms — are not
 * here: they belong to the rental system that raises the money and offers
 * the work, so they went to the RMS with it and live on
 * `app.hireos.co.nz`. `/result` went with them: every outcome it renders
 * (quote approved, deposit paid, contractor booked, invoice sent) is one
 * of those pages finishing, and none of them exists in this repo any more.
 * Links already sent to Customers and Contractors point at this host, so
 * `firebase.json` 301s each of those paths across with its query intact —
 * the token is in the query, so a redirect that dropped it would strand
 * every link in flight.
 *
 * An Inquiry's own quote status is not a route: it is polled in place on
 * the confirmation step (`InquirySuccess`, `GET /api/inquiry/status`), so
 * the Customer never leaves the flow they just finished.
 *
 * `/` is the one host-dependent route (#221): on a tenant subdomain it is the
 * inquiry flow, on the apex it is the HireOS identity page. The apex has no
 * tenant to resolve, so its `/enquiry` would fail closed anyway — sending the
 * apex there left the domain with no root, which is the shape Safe Browsing
 * flagged.
 *
 * The flow is `/enquiry` (#140): the product is sold in New Zealand, and
 * the spelling a Customer reads is the one their Company uses. It was
 * `/inquiry` until then, and Companies put that on their own websites and
 * in their email signatures, so the old address goes to the page with its
 * query intact rather than to the root.
 */
function RouteShell(): React.JSX.Element {
  return (
    <>
      <Outlet />
      <ScrollRestoration />
    </>
  );
}

function RedirectToInquiry(): React.JSX.Element {
  return <Navigate to="/enquiry" replace />;
}

/** `/` — identity page on the apex, the inquiry flow on a tenant host. */
function RootRoute(): React.JSX.Element {
  return isPublicSiteHost(window.location.hostname) ? (
    <HomePage />
  ) : (
    <RedirectToInquiry />
  );
}

/** The flow's old address, `/inquiry`, sent on with its query. */
function OldInquiryPath(): React.JSX.Element {
  const { search } = useLocation();
  return <Navigate to={`/enquiry${search}`} replace />;
}

/**
 * Unknown paths land on that host's root rather than always on `/enquiry`:
 * on the apex there is no tenant, so `/enquiry` renders an error instead of a
 * page.
 */
function NotFoundRoute(): React.JSX.Element {
  return isPublicSiteHost(window.location.hostname) ? (
    <Navigate to="/" replace />
  ) : (
    <RedirectToInquiry />
  );
}

export const router = createBrowserRouter([
  {
    element: <RouteShell />,
    children: [
      {
        path: "/",
        element: <RootRoute />,
      },
      {
        // Pathless layout: turns on full Session Replay for the whole
        // inquiry funnel.
        element: <InquiryReplayBoundary />,
        children: [
          {
            path: "/enquiry",
            element: <InquiryPage />,
          },
        ],
      },
      {
        path: "/inquiry",
        element: <OldInquiryPath />,
      },
      {
        path: "/terms",
        element: <TermsPage />,
      },
      {
        path: "/privacy",
        element: <PrivacyPage />,
      },
      {
        path: "*",
        element: <NotFoundRoute />,
      },
    ],
  },
]);
